import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'cookieConsent';
const CONSENT_ACCEPTED = 'accepted';
const CONSENT_REJECTED = 'rejected';
const GA_MEASUREMENT_ID = 'G-C9CZCC2CS4';

const disableGoogleAnalytics = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;

  if (typeof document === 'undefined') {
    return;
  }

  const cookieNames = document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0]?.trim())
    .filter((name) => name && name.startsWith('_ga'));

  cookieNames.forEach((name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  });
};

const loadGoogleAnalytics = () => {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.gtag || window.__gaLoaded) {
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID);
  window.__gaLoaded = true;
};

export default function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const existing = window.localStorage.getItem(STORAGE_KEY);
    if (!existing) {
      setIsVisible(true);
    }

    if (existing === CONSENT_ACCEPTED) {
      loadGoogleAnalytics();
    }

    if (existing === CONSENT_REJECTED) {
      disableGoogleAnalytics();
    }

    const openHandler = () => {
      setIsVisible(true);
    };

    window.addEventListener('cookie-consent:open', openHandler);
    return () => window.removeEventListener('cookie-consent:open', openHandler);
  }, []);

  const handleChoice = (choice) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, choice);
    }

    if (choice === CONSENT_ACCEPTED) {
      loadGoogleAnalytics();
    } else {
      disableGoogleAnalytics();
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('cookie-consent:updated'));
    }

    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed inset-x-0 bottom-4 z-50 px-4">
      <div className="max-w-4xl mx-auto border border-stone-200 bg-[#f5f4f2] shadow-lg">
        <div className="p-6 md:flex md:items-center md:justify-between gap-6">
          <div className="text-[#2d2b29]">
            <p className="text-xs uppercase tracking-[0.3em] text-[#7b7671] mb-3">
              Cookie Preferences
            </p>
            <p className="text-sm md:text-base text-[#5f5b56] leading-relaxed">
              We use cookies to measure site performance and improve your experience. In the EEA,
              UK, and Switzerland we only run analytics after you opt in. You can change your
              preferences anytime.
            </p>
            <div className="mt-3 text-sm text-[#5f5b56]">
              <Link to="/privacy" className="underline underline-offset-4 hover:text-[#2d2b29]">
                Read the privacy policy
              </Link>
            </div>
          </div>
          <div className="mt-4 md:mt-0 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => handleChoice(CONSENT_REJECTED)}
              className="px-5 py-3 border border-stone-400 text-sm uppercase tracking-[0.2em] text-[#2d2b29] hover:bg-white transition-colors"
            >
              Reject Optional
            </button>
            <button
              type="button"
              onClick={() => handleChoice(CONSENT_ACCEPTED)}
              className="px-5 py-3 bg-[#1c1b1a] text-white text-sm uppercase tracking-[0.2em] hover:bg-[#2d2b29] transition-colors"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
